import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";
import { ThreadEnvMode, TrimmedNonEmptyString, TrimmedString } from "./baseSchemas";
import {
  ClaudeModelOptionsObject,
  CodexModelOptionsObject,
  DEFAULT_GIT_TEXT_GENERATION_MODEL_BY_PROVIDER,
} from "./model";
import { ModelSelection } from "./orchestration";
import { ThemeId } from "./theme";

// ── Client Settings (local-only) ───────────────────────────────

/**
 * Plan-approval policy for master-initiated multi-worker plans (M4.5).
 * - `'require'` (default): always show the approval UI before spawning workers.
 * - `'autoAtWorkspace'`: auto-approve within the current workspace.
 * - `'autoEverywhere'`: auto-approve everywhere (fully automated).
 */
export const PlanApprovalMode = Schema.Literals(["require", "autoAtWorkspace", "autoEverywhere"]);
export type PlanApprovalMode = typeof PlanApprovalMode.Type;
export const DEFAULT_PLAN_APPROVAL_MODE: PlanApprovalMode = "require";

export const TimestampFormat = Schema.Literals(["locale", "12-hour", "24-hour"]);
export type TimestampFormat = typeof TimestampFormat.Type;
export const DEFAULT_TIMESTAMP_FORMAT: TimestampFormat = "locale";

/**
 * How the left sidebar organizes work.
 * - `'activity'` (default): threads are the spine. One recency-ordered list —
 *   pinned on top, done parked at the bottom — with the project picker acting
 *   as a filter over it. Carries no runtime chrome.
 * - `'projects'`: projects are the spine. One row per project with its threads
 *   nested underneath, plus environment grouping and runtime controls.
 */
export const SidebarLayoutMode = Schema.Literals(["activity", "projects"]);
export type SidebarLayoutMode = typeof SidebarLayoutMode.Type;
export const DEFAULT_SIDEBAR_LAYOUT_MODE: SidebarLayoutMode = "activity";

export const SidebarProjectSortOrder = Schema.Literals(["updated_at", "created_at", "manual"]);
export type SidebarProjectSortOrder = typeof SidebarProjectSortOrder.Type;
export const DEFAULT_SIDEBAR_PROJECT_SORT_ORDER: SidebarProjectSortOrder = "updated_at";

export const SidebarThreadSortOrder = Schema.Literals(["updated_at", "created_at"]);
export type SidebarThreadSortOrder = typeof SidebarThreadSortOrder.Type;
export const DEFAULT_SIDEBAR_THREAD_SORT_ORDER: SidebarThreadSortOrder = "updated_at";

/**
 * Fallback route for new work when a selected project or thread does not already
 * identify its execution environment. Forge is multi-runtime capable; this value
 * must not be treated as a one-time account onboarding choice.
 */
export const ThreadRuntimeOption = Schema.Literals(["cloud", "desktop-local"]);
export type ThreadRuntimeOption = typeof ThreadRuntimeOption.Type;
export const DEFAULT_THREAD_RUNTIME: ThreadRuntimeOption = "cloud";

// ── Agent Orchestration Settings ──────────────────────────────────────────────

export const AgentOrchestrationSettingsSchema = Schema.Struct({
  /** Plan-approval policy for master-initiated multi-worker plans (M4.5 + M5). */
  planApproval: PlanApprovalMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PLAN_APPROVAL_MODE)),
  ),
  workerSpawn: Schema.Literals(["require", "autoAfterPlan", "autoEverywhere"]).pipe(
    Schema.withDecodingDefault(Effect.succeed("autoAfterPlan" as const)),
  ),
  autoMergeWorkers: Schema.Literals(["linear", "rebaseOnConflict", "ask"]).pipe(
    Schema.withDecodingDefault(Effect.succeed("rebaseOnConflict" as const)),
  ),
  stuckTimeoutMin: Schema.Number.pipe(Schema.withDecodingDefault(Effect.succeed(30))),
  workerDirectMessaging: Schema.Literals(["warn", "allow", "block"]).pipe(
    Schema.withDecodingDefault(Effect.succeed("warn" as const)),
  ),
  notificationDedupeWindowSec: Schema.Number.pipe(Schema.withDecodingDefault(Effect.succeed(60))),
  maxWorkersPerMaster: Schema.Number.pipe(Schema.withDecodingDefault(Effect.succeed(5))),
});
export type AgentOrchestrationSettings = typeof AgentOrchestrationSettingsSchema.Type;

// ── CMMD Sync Settings ─────────────────────────────────────────────────────────

export const CmmdSyncSettingsSchema = Schema.Struct({
  autoCreateTasks: Schema.Literals(["ask", "always", "never"]).pipe(
    Schema.withDecodingDefault(Effect.succeed("ask" as const)),
  ),
  pushLocalProject: Schema.Literals(["ask", "always", "never"]).pipe(
    Schema.withDecodingDefault(Effect.succeed("ask" as const)),
  ),
  // Default false during S1 — CMMD task status auto-sync is gated by the
  // `cmmd_task_status_auto_sync` feature flag and not visible in settings UI
  // when the flag is off. Defaulting to false keeps server-side auto-sync
  // behavior naturally off until both the flag is enabled AND the user opts
  // in via the (then-visible) toggle.
  autoSyncTaskStatus: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
});
export type CmmdSyncSettings = typeof CmmdSyncSettingsSchema.Type;

// ── Agent Context Settings ─────────────────────────────────────────────────────

export const AgentContextSettingsSchema = Schema.Struct({
  brainContextAlways: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  webSearchEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  brainMaxTokens: Schema.Number.pipe(Schema.withDecodingDefault(Effect.succeed(2000))),
  webMaxResults: Schema.Number.pipe(Schema.withDecodingDefault(Effect.succeed(5))),
});
export type AgentContextSettings = typeof AgentContextSettingsSchema.Type;

// ── Browser Settings ─────────────────────────────────────────────────────────

export const BrowserApprovalMode = Schema.Literals(["alwaysAsk", "askUnknown", "neverAsk"]);
export type BrowserApprovalMode = typeof BrowserApprovalMode.Type;

export const BrowserAnnotationScreenshotMode = Schema.Literals(["always", "ask", "never"]);
export type BrowserAnnotationScreenshotMode = typeof BrowserAnnotationScreenshotMode.Type;

export const BrowserSessionRetentionMode = Schema.Literals(["closeOnThreadEnd", "keepForHandoff"]);
export type BrowserSessionRetentionMode = typeof BrowserSessionRetentionMode.Type;

export const PreviewInteractionMode = Schema.Literals(["interactive", "annotate"]);
export type PreviewInteractionMode = typeof PreviewInteractionMode.Type;
export const DEFAULT_PREVIEW_INTERACTION_MODE: PreviewInteractionMode = "interactive";

export const BrowserSettingsSchema = Schema.Struct({
  enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  approvalMode: BrowserApprovalMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("neverAsk" as const)),
  ),
  annotationScreenshots: BrowserAnnotationScreenshotMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("always" as const)),
  ),
  sessionRetention: BrowserSessionRetentionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("keepForHandoff" as const)),
  ),
  allowedDomains: Schema.Array(TrimmedNonEmptyString).pipe(
    Schema.withDecodingDefault(Effect.succeed([] as readonly string[])),
  ),
  blockedDomains: Schema.Array(TrimmedNonEmptyString).pipe(
    Schema.withDecodingDefault(Effect.succeed([] as readonly string[])),
  ),
});
export type BrowserSettings = typeof BrowserSettingsSchema.Type;

// ── Client Settings ────────────────────────────────────────────────────────────

export const ThemePreference = Schema.Literals(["system", "light", "dark"]);
export type ThemePreference = typeof ThemePreference.Type;

/**
 * How a thread announces attention and completion: silence, desktop
 * notifications, a sound, or both at once. t3code's mode set, adopted
 * verbatim so the settings surface and coordinator branch on the same
 * four values.
 */
export const NotificationMode = Schema.Literals([
  "off",
  "notifications",
  "sound",
  "notifications-and-sound",
]);
export type NotificationMode = typeof NotificationMode.Type;
export const MIN_APP_FONT_SIZE = 11;
export const MAX_APP_FONT_SIZE = 20;

/**
 * The interface size drives `html { font-size }`, so it is the root every
 * rem-based dimension in the app is measured against — spacing, radii, control
 * heights and the whole type scale, not just body text.
 *
 * 16 is the browser's own default and the size the reference design system was
 * drawn at, which is why `size-11` is a real 44px here and `text-base` is a
 * real 16px. The app previously ran at 14, which quietly rendered every ported
 * measurement ~12.5% smaller than the drawing it came from.
 */
export const DEFAULT_UI_FONT_SIZE = 16;

/**
 * Deliberately *below* the interface size. Code is scanned in dense columns
 * rather than read as prose, and monospace faces carry more ink per em than
 * the UI face at the same nominal size, so matching the two makes code look
 * oversized. 13 against a 16 interface is the reference's ratio.
 */
export const DEFAULT_CODE_FONT_SIZE = 13;

/**
 * Bumped whenever a font default moves.
 *
 * It exists because these values are *materialised* into storage rather than
 * left absent: `useUpdateSettings` persists the whole merged snapshot, so the
 * first time a user changes any client setting, today's defaults are written
 * out as if they had been chosen. Without a marker there is no way to tell a
 * frozen default from a deliberate pick, so raising a default would reach only
 * fresh installs — and a migration that ignored the marker would re-apply
 * itself forever, making the old value permanently unselectable.
 */
export const FONT_DEFAULTS_VERSION = 2;
export const AppFontSize = Schema.Int.check(Schema.isGreaterThanOrEqualTo(MIN_APP_FONT_SIZE)).check(
  Schema.isLessThanOrEqualTo(MAX_APP_FONT_SIZE),
);
export type AppFontSize = typeof AppFontSize.Type;

/**
 * Per-surface font sizes, in absolute px.
 *
 * Deliberately NOT rem. The interface size already scales the root, so a
 * rem-based prompt or code size would scale twice — pick 20px interface and a
 * "14px" code block would render at 17.5.
 *
 * Ranges are wider than the reference's on purpose. Ours has to contain every
 * value an existing install may already hold: the reference floors the
 * interface at 12 where we have shipped 11 since launch, and a stored value
 * outside the schema's range does not fall back, it throws — taking the whole
 * settings object with it. `normalizeClientSettingsInput` clamps as a second
 * line of defence, but the range is the first.
 */
export const MIN_PROMPT_FONT_SIZE = 11;
export const MAX_PROMPT_FONT_SIZE = 20;
export const DEFAULT_PROMPT_FONT_SIZE = 14;
export const PromptFontSize = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(MIN_PROMPT_FONT_SIZE),
).check(Schema.isLessThanOrEqualTo(MAX_PROMPT_FONT_SIZE));

export const MIN_TERMINAL_FONT_SIZE = 8;
export const MAX_TERMINAL_FONT_SIZE = 20;
export const DEFAULT_TERMINAL_FONT_SIZE = 12;
export const TerminalFontSize = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(MIN_TERMINAL_FONT_SIZE),
).check(Schema.isLessThanOrEqualTo(MAX_TERMINAL_FONT_SIZE));

/**
 * A user-typed font family list.
 *
 * Empty means "the default stack" rather than "no font". A custom family is
 * always *prepended* to the shipped stack at apply time, never substituted for
 * it, so choosing a display face cannot cost glyph coverage for scripts it
 * does not carry.
 */
export const FontFamilyPreference = Schema.String.check(Schema.isMaxLength(200));
export type FontFamilyPreference = typeof FontFamilyPreference.Type;

/**
 * How solid glass surfaces are, as a percentage.
 *
 * 100 is opaque. The floor is 40 rather than 0 because glass is a legibility
 * surface before it is a decoration: below roughly 40% the canvas reads
 * through menus and the composer far enough to hurt text contrast, so it is
 * not a range the user should be able to reach. Stepped by 5 in the UI —
 * finer than that is not perceptible on the surfaces it drives.
 *
 * The default matches the value `index.css` already ships, so introducing the
 * setting changes nothing until someone moves it.
 */
/**
 * Days of silence after which a thread settles into the shelf on its own.
 *
 * Null means never — only an explicit settle or a terminal status moves a
 * thread. That has to be expressible, because this is a judgement about
 * someone's working rhythm, and a person who keeps a week of threads warm is
 * not doing it wrong.
 */
export const MIN_AUTO_SETTLE_AFTER_DAYS = 1;
export const MAX_AUTO_SETTLE_AFTER_DAYS = 90;
export const DEFAULT_AUTO_SETTLE_AFTER_DAYS = 3;
export const AutoSettleAfterDays = Schema.NullOr(
  Schema.Int.check(Schema.isGreaterThanOrEqualTo(MIN_AUTO_SETTLE_AFTER_DAYS)).check(
    Schema.isLessThanOrEqualTo(MAX_AUTO_SETTLE_AFTER_DAYS),
  ),
);

export const MIN_GLASS_OPACITY = 40;
export const MAX_GLASS_OPACITY = 100;
export const DEFAULT_GLASS_OPACITY = 80;
export const GlassOpacity = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(MIN_GLASS_OPACITY),
).check(Schema.isLessThanOrEqualTo(MAX_GLASS_OPACITY));

export const MIN_APPEARANCE_CONTRAST = 50;
export const MAX_APPEARANCE_CONTRAST = 200;
export const DEFAULT_APPEARANCE_CONTRAST = 100;
/**
 * Interface contrast, in percent of the shipped look. 100 disables mixing;
 * above it the `--contrast-*` axis in `apps/web/src/index.css` raises text
 * and border contrast from the semantic roles, below it softens them.
 * Upstream's axis and bounds, adopted by the token union.
 */
export const AppearanceContrast = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_APPEARANCE_CONTRAST, maximum: MAX_APPEARANCE_CONTRAST }),
);
export type AppearanceContrast = typeof AppearanceContrast.Type;

export const ClientSettingsSchema = Schema.Struct({
  theme: ThemePreference.pipe(
    Schema.withDecodingDefault(Effect.succeed<ThemePreference>("system")),
  ),
  /**
   * The named palette, deliberately a separate axis from `theme`.
   *
   * `theme` is the appearance (light/dark/follow the system); `themeId` is
   * which colours that appearance is rendered in. Collapsing them into one
   * setting means picking a palette silently changes whether you are in dark
   * mode, which is a bug the reference implementation had to fix after
   * shipping. Defaults to `forge`, which applies no palette at all.
   */
  themeId: ThemeId,
  uiFontSize: AppFontSize.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_UI_FONT_SIZE))),
  codeFontSize: AppFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_CODE_FONT_SIZE)),
  ),
  /** See `FONT_DEFAULTS_VERSION`. Written on every save, read before decode. */
  fontDefaultsVersion: Schema.Int.pipe(
    Schema.withDecodingDefault(Effect.succeed(FONT_DEFAULTS_VERSION)),
  ),
  /** The composer's own size, independent of the interface scale around it. */
  promptFontSize: PromptFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROMPT_FONT_SIZE)),
  ),
  /**
   * The terminal's size. Separate from `codeFontSize` because a terminal is a
   * character grid: its size decides how many columns fit, which is a
   * different judgement from how large a diff should read.
   */
  terminalFontSize: TerminalFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_TERMINAL_FONT_SIZE)),
  ),
  /** Empty means the shipped stack. See `FontFamilyPreference`. */
  fontFamilySans: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  fontFamilyCode: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  fontFamilyComposer: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  fontFamilyTerminal: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  /**
   * Grayscale `-webkit-font-smoothing: antialiased` — thinner stems. Off
   * restores the platform default, which macOS renders with heavier stem
   * darkening. Only Apple engines honour the property, so this is inert
   * elsewhere by design rather than by omission.
   */
  fontSmoothing: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  /** See `AutoSettleAfterDays`. */
  sidebarAutoSettleAfterDays: AutoSettleAfterDays.pipe(
    Schema.withDecodingDefault(Effect.succeed<number | null>(DEFAULT_AUTO_SETTLE_AFTER_DAYS)),
  ),
  /** See `GlassOpacity`. */
  glassOpacity: GlassOpacity.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_GLASS_OPACITY)),
  ),
  /** See `AppearanceContrast`. */
  appearanceContrast: AppearanceContrast.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_APPEARANCE_CONTRAST)),
  ),
  confirmThreadArchive: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  confirmThreadDelete: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  /**
   * How the thread notifies about attention and completion. `off` silences
   * everything; the other modes combine an in-app toast path with the desktop
   * Notification permission flow and a WebAudio chime. Defaults off so the
   * surface is opt-in, matching t3code.
   */
  notificationMode: NotificationMode.pipe(
    Schema.withDecodingDefault(Effect.succeed<NotificationMode>("off")),
  ),
  /** In-app toasts for thread transitions, independent of the mode above. */
  inAppNotificationsEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  showInteractionModeToggle: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  /**
   * Rest the Composer to one line when the editor loses focus.
   *
   * Defaults on, matching t3code. Off keeps the Expanded Composer until a
   * scroll gesture asks for the space, so a person who finds the movement
   * distracting can stop the blur half of it without losing the other half.
   */
  composerCollapseOnBlur: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  diffWordWrap: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  previewAutoOpen: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  previewRememberLastUrl: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  previewFollowAgentTab: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  previewLastUrl: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  previewInteractionMode: PreviewInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PREVIEW_INTERACTION_MODE)),
  ),
  sidebarLayoutMode: SidebarLayoutMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_LAYOUT_MODE)),
  ),
  sidebarProjectSortOrder: SidebarProjectSortOrder.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_PROJECT_SORT_ORDER)),
  ),
  sidebarThreadSortOrder: SidebarThreadSortOrder.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_THREAD_SORT_ORDER)),
  ),
  sidebarWorkspaceOrder: Schema.Array(Schema.String).pipe(
    Schema.withDecodingDefault(Effect.succeed([] as readonly string[])),
  ),
  /**
   * Legacy sidebar (forge's original per-project thread tree). Deliberately a
   * fresh key mirroring upstream's own escape hatch: false keeps sidebar v2
   * (project groups, search, pin and snooze shelves, drag reorder) the
   * default for everyone, true restores the v1 layout. Resolved only after
   * client settings hydrate so the sidebar never remounts on hydration.
   */
  legacySidebarEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  timestampFormat: TimestampFormat.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_TIMESTAMP_FORMAT)),
  ),
  defaultThreadRuntime: ThreadRuntimeOption.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_THREAD_RUNTIME)),
  ),
  agentOrchestration: AgentOrchestrationSettingsSchema.pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),
  cmmdSync: CmmdSyncSettingsSchema.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  agentContext: AgentContextSettingsSchema.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  /**
   * ISO timestamp of when this browser finished (or skipped) first-run
   * onboarding. Null until the welcome wizard completes. Browser-local by
   * design: workspace evidence, not this flag, decides a first visit on a
   * second device, so a returning browser reads its own flag and skips
   * straight through.
   */
  onboardingCompletedAt: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
});
export type ClientSettings = typeof ClientSettingsSchema.Type;

export const DEFAULT_CLIENT_SETTINGS: ClientSettings = Schema.decodeSync(ClientSettingsSchema)({});

// ── Server Settings (server-authoritative) ────────────────────

// Defined in `baseSchemas` so `orchestration.ts` can use it without closing an
// import cycle through this file; re-exported here because this is where every
// existing caller imports it from.
export { ThreadEnvMode } from "./baseSchemas";

/**
 * Chooses the GitHub ACTOR for Forge-initiated pushes, pull requests, and
 * commit identity — i.e. WHO shows up as the author/committer on GitHub.
 * This is independent of `coauthorIncludeForge` below, which only decorates
 * a commit's trailer text; `githubWriteIdentity` decides whose credentials
 * and git identity Forge actually uses to write.
 *   - `"forge-bot"` (default): Forge must act as the cmmd-forge GitHub App
 *     bot. If a brokered bot-identity token isn't available, Forge fails
 *     closed instead of silently substituting the human's own GitHub
 *     credentials.
 *   - `"my-account"`: Forge deliberately acts as the connected human GitHub
 *     account. This is an explicit, supported choice — no fail-closed
 *     gating and no warning.
 */
export const GithubWriteIdentity = Schema.Literals(["forge-bot", "my-account"]);
export type GithubWriteIdentity = typeof GithubWriteIdentity.Type;
export const DEFAULT_GITHUB_WRITE_IDENTITY: GithubWriteIdentity = "forge-bot";

export const ProviderCredentialScope = Schema.Literals(["shared", "user", "proxied"]);
export type ProviderCredentialScope = typeof ProviderCredentialScope.Type;

export const ProviderCredentialSource = Schema.Literals([
  "forge-managed",
  "server-settings",
  "environment",
  "process-default",
]);
export type ProviderCredentialSource = typeof ProviderCredentialSource.Type;

const ProviderCredentialSettings = Schema.Struct({
  scope: ProviderCredentialScope.pipe(
    Schema.withDecodingDefault(Effect.succeed("shared" as const)),
  ),
  source: ProviderCredentialSource.pipe(
    Schema.withDecodingDefault(Effect.succeed("server-settings" as const)),
  ),
});
export type ProviderCredentialSettings = typeof ProviderCredentialSettings.Type;

const makeBinaryPathSetting = (fallback: string) =>
  TrimmedString.pipe(
    Schema.decodeTo(
      Schema.String,
      SchemaTransformation.transformOrFail({
        decode: (value) => Effect.succeed(value || fallback),
        encode: (value) => Effect.succeed(value),
      }),
    ),
    Schema.withDecodingDefault(Effect.succeed(fallback)),
  );

/**
 * Provider-neutral personal/workspace access mode. Accepts the legacy Codex
 * value `chatgpt` on decode and normalizes it to `personal` so persisted
 * settings keep loading; new writes use `personal`.
 */
const CodexAccessMode = Schema.Literals(["workspace", "chatgpt", "personal"]).pipe(
  Schema.decodeTo(
    Schema.Literals(["workspace", "personal"]),
    SchemaTransformation.transformOrFail({
      decode: (value) => Effect.succeed(value === "chatgpt" ? ("personal" as const) : value),
      encode: (value) => Effect.succeed(value),
    }),
  ),
);

export const CodexSettings = Schema.Struct({
  enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  binaryPath: makeBinaryPathSetting("codex"),
  homePath: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  accessMode: CodexAccessMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("workspace" as const)),
  ),
  credentials: ProviderCredentialSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  customModels: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
});
export type CodexSettings = typeof CodexSettings.Type;

export const ClaudeEndpointProfile = Schema.Struct({
  slug: TrimmedNonEmptyString,
  displayName: Schema.optionalKey(Schema.String),
  /** User-facing capability description shown in tooltips. Never expose slug or baseUrl to users. */
  description: Schema.optional(Schema.String),
  apiModelId: Schema.optionalKey(Schema.String),
  baseUrl: Schema.String,
  apiKey: Schema.String,
  disableToolSearch: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  effortLevel: Schema.optional(Schema.String),
});
export type ClaudeEndpointProfile = typeof ClaudeEndpointProfile.Type;

// Each Apex profile maps a forge-stable `slug` to an upstream model exposed
// via an Anthropic-compatible endpoint (DeepSeek's /anthropic, Moonshot's
// /anthropic). `apiModelId` is the actual model name the upstream expects.
// The ApexAdapter routes these through the Claude Agent SDK with
// ANTHROPIC_BASE_URL / ANTHROPIC_AUTH_TOKEN overrides per profile.
//
// `description` is user-facing; per the schema comment above, NEVER leak
// the upstream model name or baseUrl into description copy.
// All Apex profiles route through https://apex.cmmd.ai — the CMMD-managed shim
// handles upstream fan-out (Mac Studio Ollama for apex-core, DeepSeek for
// apex-flash + apex-reason, Moonshot for apex-general). Clients never see
// upstream model IDs, never need per-upstream API keys.
export const DEFAULT_APEX_PROFILES: ReadonlyArray<ClaudeEndpointProfile> = [
  {
    slug: "apex-core",
    displayName: "Apex Core",
    description: "Deep, business-grade model — large refactors, long context, tool use.",
    apiModelId: "apex-core",
    baseUrl: "https://apex.cmmd.ai",
    apiKey: "",
    disableToolSearch: false,
  },
  {
    slug: "apex-flash",
    displayName: "Apex Flash",
    description: "Fast iteration — quick edits, autocomplete, snappy chat.",
    apiModelId: "apex-flash",
    baseUrl: "https://apex.cmmd.ai",
    apiKey: "",
    disableToolSearch: false,
  },
  {
    slug: "apex-general",
    displayName: "Apex General",
    description: "Balanced everyday model — strong tool use, good for most coding sessions.",
    apiModelId: "apex-general",
    baseUrl: "https://apex.cmmd.ai",
    apiKey: "",
    disableToolSearch: false,
  },
  {
    slug: "apex-reason",
    displayName: "Apex Reason",
    description: "Deep reasoning — debugging, architecture, multi-step problems.",
    apiModelId: "apex-reason",
    baseUrl: "https://apex.cmmd.ai",
    apiKey: "",
    disableToolSearch: false,
    effortLevel: "max",
  },
];

export const ClaudeSettings = Schema.Struct({
  enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  binaryPath: makeBinaryPathSetting("claude"),
  configPath: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  credentials: ProviderCredentialSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  customModels: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
  anthropicBaseUrl: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  anthropicApiKey: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  endpointProfiles: Schema.Array(ClaudeEndpointProfile).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
});
export type ClaudeSettings = typeof ClaudeSettings.Type;

export const OllamaSettings = Schema.Struct({
  enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  baseUrl: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed("http://localhost:11434"))),
  customModels: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
});
export type OllamaSettings = typeof OllamaSettings.Type;

export const CmmdSettings = Schema.Struct({
  enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  baseUrl: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed("http://localhost:4000"))),
  apiKey: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
});
export type CmmdSettings = typeof CmmdSettings.Type;

export const ApexSettings = Schema.Struct({
  enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  baseUrl: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed("https://apex.cmmd.ai"))),
  credentials: ProviderCredentialSettings.pipe(
    Schema.withDecodingDefault(Effect.succeed({ source: "forge-managed" as const })),
  ),
  customModels: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
  disabledProfiles: Schema.Array(Schema.String).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
});
export type ApexSettings = typeof ApexSettings.Type;

// Cursor + OpenCode both ship enabled by default; CursorProviderLive and
// OpenCodeProviderLive auto-probe their CLIs alongside Codex/Claude. Each
// reports "Not installed" if its binary isn't on PATH, "Not connected"
// without an API key, "Authenticated" with a key.
export const CursorSettings = Schema.Struct({
  enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  binaryPath: makeBinaryPathSetting("cursor-agent"),
  credentials: ProviderCredentialSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  // Override the Cursor API endpoint — leave blank to use the upstream default.
  // Mirrors the upstream CursorSettings.apiEndpoint and is consumed by the ACP-based
  // adapter when it constructs the cursor-agent spawn environment.
  apiEndpoint: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  customModels: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
});
export type CursorSettings = typeof CursorSettings.Type;

export const OpenCodeSettings = Schema.Struct({
  enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  binaryPath: makeBinaryPathSetting("opencode"),
  credentials: ProviderCredentialSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  // Optional pre-existing opencode serve URL — leave blank to let Forge spawn
  // the server lazily on first turn (matching the upstream OpenCodeSettings.serverUrl).
  serverUrl: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  // Basic-auth password for an externally-managed opencode server. Stored in
  // plain text on disk (the same caveat the upstream implementation carries).
  serverPassword: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  customModels: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
});
export type OpenCodeSettings = typeof OpenCodeSettings.Type;

export const ObservabilitySettings = Schema.Struct({
  otlpTracesUrl: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  otlpMetricsUrl: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  otlpLogsUrl: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
});
export type ObservabilitySettings = typeof ObservabilitySettings.Type;

export const PluginSkillPermissions = Schema.Record(
  TrimmedNonEmptyString,
  Schema.Record(TrimmedNonEmptyString, Schema.Boolean),
).pipe(Schema.withDecodingDefault(Effect.succeed({})));
export type PluginSkillPermissions = typeof PluginSkillPermissions.Type;

export const FirstAccountSetupStepId = Schema.Literals([
  "account",
  "usage",
  "providers",
  "projects",
  "capabilities",
  "first-run",
]);
export type FirstAccountSetupStepId = typeof FirstAccountSetupStepId.Type;

export const FirstAccountSetupStatus = Schema.Literals(["pending", "in_progress", "completed"]);
export type FirstAccountSetupStatus = typeof FirstAccountSetupStatus.Type;

export const FirstAccountSetupState = Schema.Struct({
  status: FirstAccountSetupStatus.pipe(
    Schema.withDecodingDefault(Effect.succeed("pending" as const)),
  ),
  activeStepId: FirstAccountSetupStepId.pipe(
    Schema.withDecodingDefault(Effect.succeed("account" as const)),
  ),
});
export type FirstAccountSetupState = typeof FirstAccountSetupState.Type;

const FirstAccountSetupStatePatch = Schema.Struct({
  status: Schema.optionalKey(FirstAccountSetupStatus),
  activeStepId: Schema.optionalKey(FirstAccountSetupStepId),
});

export const ServerSettings = Schema.Struct({
  enableAssistantStreaming: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  defaultThreadEnvMode: ThreadEnvMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("worktree" as const satisfies ThreadEnvMode)),
  ),
  textGenerationModelSelection: ModelSelection.pipe(
    Schema.withDecodingDefault(
      Effect.succeed({
        provider: "codex" as const,
        model: DEFAULT_GIT_TEXT_GENERATION_MODEL_BY_PROVIDER.codex,
      }),
    ),
  ),
  // When true, Forge-generated commits include a Co-Authored-By trailer
  // referencing the cmmd-forge GitHub App bot user. GitHub renders the
  // bot's avatar (the Forge flame) next to the human author. This only
  // decorates the commit message — it does NOT decide who pushes or opens
  // the PR. See `githubWriteIdentity` below for that decision; the two
  // settings are independent and every combination is valid.
  coauthorIncludeForge: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),

  // See the `GithubWriteIdentity` doc comment above for the full contract.
  // Decides WHO Forge acts as when writing to GitHub (pushes, PRs, commit
  // identity) — independent of `coauthorIncludeForge` above.
  githubWriteIdentity: GithubWriteIdentity.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_GITHUB_WRITE_IDENTITY)),
  ),

  // ADR-011 (CMMD-Center/CodeWeb): when true, Forge injects CodeWeb's remote
  // MCP server (`codeweb`, HTTP) natively into gated agent sessions so the
  // CodeWeb `map_*` tools exist with no repo-local `.mcp.json`. Default OFF:
  // the rollout is flag-gated, CMMD platform org first, then everyone. The
  // generic `.mcp.json` path stays for external clients.
  codewebMcp: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),

  // Provider specific settings
  providers: Schema.Struct({
    codex: CodexSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    claudeAgent: ClaudeSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    ollama: OllamaSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    cmmd: CmmdSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    apex: ApexSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    cursor: CursorSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    opencode: OpenCodeSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  }).pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  observability: ObservabilitySettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  browser: BrowserSettingsSchema.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  pluginSkillPermissions: PluginSkillPermissions,
  firstAccountSetup: FirstAccountSetupState.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
});
export type ServerSettings = typeof ServerSettings.Type;

export const DEFAULT_SERVER_SETTINGS: ServerSettings = Schema.decodeSync(ServerSettings)({});

export class ServerSettingsError extends Schema.TaggedErrorClass<ServerSettingsError>()(
  "ServerSettingsError",
  {
    settingsPath: Schema.String,
    detail: Schema.String,
    cause: Schema.optional(Schema.Defect),
  },
) {
  override get message(): string {
    return `Server settings error at ${this.settingsPath}: ${this.detail}`;
  }
}

// ── Unified type ─────────────────────────────────────────────────────

export type UnifiedSettings = ServerSettings & ClientSettings;
export const DEFAULT_UNIFIED_SETTINGS: UnifiedSettings = {
  ...DEFAULT_SERVER_SETTINGS,
  ...DEFAULT_CLIENT_SETTINGS,
};

// ── Server Settings Patch (replace with a Schema.deepPartial if available) ──────────────────────────────────────────

const CodexModelOptionsPatch = Schema.Struct({
  reasoningEffort: Schema.optionalKey(CodexModelOptionsObject.fields.reasoningEffort),
  fastMode: Schema.optionalKey(CodexModelOptionsObject.fields.fastMode),
});

const ClaudeModelOptionsPatch = Schema.Struct({
  thinking: Schema.optionalKey(ClaudeModelOptionsObject.fields.thinking),
  effort: Schema.optionalKey(ClaudeModelOptionsObject.fields.effort),
  fastMode: Schema.optionalKey(ClaudeModelOptionsObject.fields.fastMode),
  contextWindow: Schema.optionalKey(ClaudeModelOptionsObject.fields.contextWindow),
});

const ModelSelectionPatch = Schema.Union([
  Schema.Struct({
    provider: Schema.optionalKey(Schema.Literal("codex")),
    model: Schema.optionalKey(TrimmedNonEmptyString),
    options: Schema.optionalKey(CodexModelOptionsPatch),
  }),
  Schema.Struct({
    provider: Schema.optionalKey(Schema.Literal("claudeAgent")),
    model: Schema.optionalKey(TrimmedNonEmptyString),
    options: Schema.optionalKey(ClaudeModelOptionsPatch),
  }),
]);

const CodexSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  binaryPath: Schema.optionalKey(Schema.String),
  homePath: Schema.optionalKey(Schema.String),
  accessMode: Schema.optionalKey(CodexAccessMode),
  credentials: Schema.optionalKey(
    Schema.Struct({
      scope: Schema.optionalKey(ProviderCredentialScope),
      source: Schema.optionalKey(ProviderCredentialSource),
    }),
  ),
  customModels: Schema.optionalKey(Schema.Array(Schema.String)),
});

const ClaudeSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  binaryPath: Schema.optionalKey(Schema.String),
  configPath: Schema.optionalKey(Schema.String),
  credentials: Schema.optionalKey(
    Schema.Struct({
      scope: Schema.optionalKey(ProviderCredentialScope),
      source: Schema.optionalKey(ProviderCredentialSource),
    }),
  ),
  customModels: Schema.optionalKey(Schema.Array(Schema.String)),
  anthropicBaseUrl: Schema.optionalKey(Schema.String),
  anthropicApiKey: Schema.optionalKey(Schema.String),
  endpointProfiles: Schema.optionalKey(Schema.Array(ClaudeEndpointProfile)),
});

const OllamaSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  baseUrl: Schema.optionalKey(Schema.String),
  customModels: Schema.optionalKey(Schema.Array(Schema.String)),
});

const CmmdSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  baseUrl: Schema.optionalKey(Schema.String),
  apiKey: Schema.optionalKey(Schema.String),
});

const ApexSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  baseUrl: Schema.optionalKey(Schema.String),
  credentials: Schema.optionalKey(
    Schema.Struct({
      scope: Schema.optionalKey(ProviderCredentialScope),
      source: Schema.optionalKey(ProviderCredentialSource),
    }),
  ),
  customModels: Schema.optionalKey(Schema.Array(Schema.String)),
  disabledProfiles: Schema.optionalKey(Schema.Array(Schema.String)),
});

const CursorSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  binaryPath: Schema.optionalKey(Schema.String),
  credentials: Schema.optionalKey(
    Schema.Struct({
      scope: Schema.optionalKey(ProviderCredentialScope),
      source: Schema.optionalKey(ProviderCredentialSource),
    }),
  ),
  apiEndpoint: Schema.optionalKey(Schema.String),
  customModels: Schema.optionalKey(Schema.Array(Schema.String)),
});

const OpenCodeSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  binaryPath: Schema.optionalKey(Schema.String),
  credentials: Schema.optionalKey(
    Schema.Struct({
      scope: Schema.optionalKey(ProviderCredentialScope),
      source: Schema.optionalKey(ProviderCredentialSource),
    }),
  ),
  serverUrl: Schema.optionalKey(Schema.String),
  serverPassword: Schema.optionalKey(Schema.String),
  customModels: Schema.optionalKey(Schema.Array(Schema.String)),
});

const BrowserSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  approvalMode: Schema.optionalKey(BrowserApprovalMode),
  annotationScreenshots: Schema.optionalKey(BrowserAnnotationScreenshotMode),
  sessionRetention: Schema.optionalKey(BrowserSessionRetentionMode),
  allowedDomains: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
  blockedDomains: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
});

export const ServerSettingsPatch = Schema.Struct({
  enableAssistantStreaming: Schema.optionalKey(Schema.Boolean),
  defaultThreadEnvMode: Schema.optionalKey(ThreadEnvMode),
  textGenerationModelSelection: Schema.optionalKey(ModelSelectionPatch),
  coauthorIncludeForge: Schema.optionalKey(Schema.Boolean),
  githubWriteIdentity: Schema.optionalKey(GithubWriteIdentity),
  codewebMcp: Schema.optionalKey(Schema.Boolean),
  browser: Schema.optionalKey(BrowserSettingsPatch),
  pluginSkillPermissions: Schema.optionalKey(PluginSkillPermissions),
  firstAccountSetup: Schema.optionalKey(FirstAccountSetupStatePatch),
  observability: Schema.optionalKey(
    Schema.Struct({
      otlpTracesUrl: Schema.optionalKey(Schema.String),
      otlpMetricsUrl: Schema.optionalKey(Schema.String),
      otlpLogsUrl: Schema.optionalKey(Schema.String),
    }),
  ),
  providers: Schema.optionalKey(
    Schema.Struct({
      codex: Schema.optionalKey(CodexSettingsPatch),
      claudeAgent: Schema.optionalKey(ClaudeSettingsPatch),
      ollama: Schema.optionalKey(OllamaSettingsPatch),
      cmmd: Schema.optionalKey(CmmdSettingsPatch),
      apex: Schema.optionalKey(ApexSettingsPatch),
      cursor: Schema.optionalKey(CursorSettingsPatch),
      opencode: Schema.optionalKey(OpenCodeSettingsPatch),
    }),
  ),
});
export type ServerSettingsPatch = typeof ServerSettingsPatch.Type;
